import { useState } from 'react'
import { useNavigate } from 'react-router'
import { switchboardApi } from '~/apis/switchboardApi'
import { toaster } from '~/components/ui/toaster'
import { ApplicationRoutes } from '~/types/RoutingTypes'

import { useDeleteSwitchboard } from '@mangovoice/switchboard-ui'

/**
 * Manages switchboard deletion logic including dialog state and API calls.
 * Handles the complete delete workflow with confirmation dialog, API call,
 * success/error toasts, and navigation.
 */
export const useSwitchboardDelete = (
  extensionDialPlan: number,
  pbxId: number | null,
  options?: {
    canDelete?: boolean
  },
) => {
  const navigate = useNavigate()
  const deleteMutation = useDeleteSwitchboard({ api: switchboardApi })
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const canDelete = options?.canDelete ?? true

  const handleDeleteClick = () => {
    if (!canDelete) {
      toaster.create({
        title: "You don't have permission to delete this switchboard.",
        type: 'error',
      })
      return
    }

    setIsDeleteDialogOpen(true)
  }

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false)
  }

  const handleDeleteConfirm = async () => {
    if (!canDelete) {
      toaster.create({
        title: "You don't have permission to delete this switchboard.",
        type: 'error',
      })
      return
    }

    if (!extensionDialPlan || !pbxId) return

    setIsDeleteDialogOpen(false)

    try {
      await deleteMutation.mutateAsync(extensionDialPlan)
      toaster.create({
        title: 'Switchboard deleted',
        type: 'success',
      })
      navigate(ApplicationRoutes.SWITCHBOARDS)
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : typeof error === 'object' && error !== null && 'message' in error
            ? String(error.message)
            : 'Unknown error'
      const errorCode =
        typeof error === 'object' && error !== null && 'code' in error
          ? String(error.code)
          : ''

      const is404 =
        (error as { status?: number })?.status === 404 ||
        errorMessage.includes('404') ||
        errorMessage.includes('Not found')
      const isPermissionDenied =
        (error as { status?: number })?.status === 403 ||
        errorCode === 'permission_denied' ||
        errorMessage.toLowerCase().includes('permission')

      toaster.create({
        title: isPermissionDenied
          ? "You don't have permission to delete this switchboard."
          : is404
            ? 'Switchboard not found (may already be deleted)'
            : 'Failed to delete switchboard',
        description: isPermissionDenied
          ? undefined
          : is404
            ? 'The switchboard does not exist in the API.'
            : errorMessage,
        type: is404 ? 'warning' : 'error',
      })
    }
  }

  return {
    isDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  }
}
